import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useProvider, useCurrentProvider } from '../hooks/useProvider';
import { useProviderStats } from '../hooks/useProviderStats';
import { useCloutReviewsEnabled } from '../hooks/useCloutReviewsEnabled';
import { ProviderWithUser } from '../services/providers';
import EndorseModal from '../components/EndorseModal';

// Layout Components
import ProviderProfileHeader from '../components/layout/ProviderProfileHeader';
import ProviderProfileNavigation from '../components/layout/ProviderProfileNavigation';
import ProviderProfileHero from '../components/layout/ProviderProfileHero';

// Provider Components
import ProviderCard from '../components/provider/ProviderCard';
import ProviderAboutSection from '../components/provider/ProviderAboutSection';
import ProviderServicesSection from '../components/provider/ProviderServicesSection';
import ProviderExperienceSection from '../components/provider/ProviderExperienceSection';
import ProviderEducationSection, {
  Education,
} from '../components/provider/ProviderEducationSection';
import ProviderEndorsedSkills from '../components/provider/ProviderEndorsedSkills';
import ProviderGoogleSection from '../components/provider/ProviderGoogleSection';
import ProviderReviews from '../components/reviews/ProviderReviews';
import ReviewInvites from '../components/reviews/ReviewInvites';
import ProviderContactInfo, { Certification } from '../components/provider/ProviderContactInfo';

const toEducation = (education: unknown[] | undefined): Education[] =>
  (Array.isArray(education) ? education : [])
    .map((edu) => {
      const e = (edu ?? {}) as Record<string, unknown>;
      return {
        institute: String(e.institution || e.institute || ''),
        degree: String(e.degree || ''),
        year: e.year ? String(e.year) : '',
      };
    })
    .filter((e) => e.institute || e.degree);

const toCertifications = (list: ProviderWithUser['certifications_list']): Certification[] =>
  (list ?? []).flatMap((cert): Certification[] => {
    if (typeof cert === 'string') return cert ? [{ title: cert }] : [];
    if (cert && typeof cert === 'object') {
      // ProfileWizard saves {name, issuer, year}
      const name = Array.isArray(cert.name) ? cert.name.join(', ') : cert.name;
      if (!name) return [];
      return [
        {
          title: name,
          issuer: cert.issuer || undefined,
          year: cert.year ? String(cert.year) : undefined,
        },
      ];
    }
    return [];
  });

const ProviderProfile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEndorseModalOpen, setIsEndorseModalOpen] = useState(false);

  // Use appropriate hook based on whether a provider id is in the URL
  const {
    provider: currentUserProvider,
    loading: currentLoading,
    error: currentError,
  } = useCurrentProvider();

  const { provider: otherProvider, loading: otherLoading, error: otherError } = useProvider(id);

  // For the own-profile checklist's "Endorse a Provider" item
  const { stats: providerStats } = useProviderStats();
  const cloutReviewsEnabled = useCloutReviewsEnabled();

  // Own profile: /profile (no id), or /provider/:id where the id is the user's own provider
  const isOwnProfile =
    !id ||
    (!!currentUserProvider && String(currentUserProvider.id) === id) ||
    (!!user && !!otherProvider && otherProvider.user_id === user.id);

  // Select the appropriate provider data
  const fetchedProvider = id ? otherProvider : currentUserProvider;
  const loading = id ? otherLoading : currentLoading;
  const error = id ? otherError : currentError;

  const handleCompleteProfile = () => {
    navigate('/profile/complete');
  };

  const handleEndorse = () => {
    setIsEndorseModalOpen(true);
  };

  // Only real data; a field with none shows "Not available" (see NotAvailable)
  const specialties = fetchedProvider?.specialties ?? [];
  const provider = fetchedProvider
    ? {
        name: fetchedProvider.business_name || fetchedProvider.user_username || 'Provider',
        location: [fetchedProvider.city, fetchedProvider.state].filter(Boolean).join(', ') || null,
        rating: fetchedProvider.clout_score || 0,
        avgRating: fetchedProvider.avg_rating,
        reviewCount: fetchedProvider.review_count,
        specialty: specialties[0] ?? null,
        status: fetchedProvider.is_certified > 0 ? 'Certified' : 'Not Certified',
        about: fetchedProvider.bio || null,
        services: specialties,
        yearsExperience: fetchedProvider.years_of_experience,
        profileImage: fetchedProvider.user_profile_image_url,
        // Read by the own-profile completion checklist
        business_name: fetchedProvider.business_name,
        user_profile_image_url: fetchedProvider.user_profile_image_url ?? undefined,
        years_of_experience: fetchedProvider.years_of_experience,
        profile_completion_percentage: fetchedProvider.profile_completion_percentage,
        education: toEducation(fetchedProvider.education),
        licenseNumber: fetchedProvider.license_number,
        certifications: toCertifications(fetchedProvider.certifications_list),
        affiliations: fetchedProvider.affiliations ?? [],
        contact: {
          // Only returned for the user's own profile (/api/providers/me)
          email: fetchedProvider.user_email,
          phone: fetchedProvider.phone,
          website: fetchedProvider.website,
        },
      }
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  if (error && id) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error loading provider: {error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-[#c6a664] text-white rounded hover:bg-[#b89854] transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // If viewing own profile but no provider profile exists yet
  if (isOwnProfile && !loading && !fetchedProvider) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-['Ovo'] text-[#1f1f1f] mb-4">No Provider Profile Found</h2>
          <p className="text-[#767472] mb-6">
            You haven't created a provider profile yet. Complete your profile to get started as a
            provider.
          </p>
          <button
            onClick={() => navigate('/profile/complete')}
            className="px-6 py-3 bg-[#c6a664] text-white font-['Open_Sans'] text-sm font-semibold uppercase hover:bg-[#b89854] transition-colors"
          >
            Complete My Profile
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="block w-full mt-3 px-6 py-3 bg-transparent border border-[#1f1f1f] text-[#1f1f1f] font-['Open_Sans'] text-sm font-semibold uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!fetchedProvider || !provider) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <ProviderProfileHeader />

      {/* Navigation */}
      <ProviderProfileNavigation />

      {/* Hero Section */}
      <ProviderProfileHero />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-0">
          {/* Left Sidebar - Provider Card */}
          <div className="w-full lg:w-[360px] lg:flex-shrink-0 order-1 lg:order-none -mt-16 sm:-mt-20 lg:-mt-0">
            <ProviderCard
              provider={provider}
              isOwnProfile={isOwnProfile}
              endorsementsGiven={providerStats?.endorsements_given ?? 0}
              onCompleteProfile={handleCompleteProfile}
              onEndorse={handleEndorse}
            />
          </div>

          {/* Right Content Area */}
          <div className="flex-1 order-2 lg:order-none">
            <div className="bg-[#fcfaf8] rounded-lg p-4 sm:p-6 lg:p-8 shadow-lg lg:border-l lg:border-[#eaeaea]">
              {/* About Section */}
              <ProviderAboutSection providerName={provider.name} about={provider.about} />

              {/* Live Google rating, and the provider's own listing picker */}
              <ProviderGoogleSection
                key={fetchedProvider.id}
                providerId={fetchedProvider.id}
                placeId={fetchedProvider.google_place_id}
                isOwnProfile={isOwnProfile}
              />

              {/* Reviews clients left on CLOUT, once they're open */}
              {cloutReviewsEnabled && (
                <>
                  <ProviderReviews
                    key={`reviews-${fetchedProvider.id}`}
                    providerId={fetchedProvider.id}
                    providerName={provider.name}
                    isOwnProfile={isOwnProfile}
                  />
                  {isOwnProfile && <ReviewInvites />}
                </>
              )}

              {/* Services Section */}
              <ProviderServicesSection services={provider.services} />

              {/* Years of Experience */}
              <ProviderExperienceSection yearsExperience={provider.yearsExperience} />

              {/* Education and Licenses */}
              <ProviderEducationSection
                education={provider.education}
                licenseNumber={provider.licenseNumber}
              />

              {/* Endorsed Skills */}
              <ProviderEndorsedSkills providerId={fetchedProvider.id} />

              {/* Certifications, Affiliations, Contact Info */}
              <ProviderContactInfo
                certifications={provider.certifications}
                affiliations={provider.affiliations}
                contact={provider.contact}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Endorse Modal */}
      <EndorseModal
        isOpen={isEndorseModalOpen}
        onClose={() => setIsEndorseModalOpen(false)}
        providerName={provider.name}
        providerId={fetchedProvider.id}
      />
    </div>
  );
};

export default ProviderProfile;
